// src/utils/feeCalculator.js
import { tuitionConfigs, getStudentPaymentSchedule } from "../data/tuitionConfigs";

export function normalizeCampusKey(campus) {
  const s = String(campus || "").toLowerCase().trim();
  if (s.includes("gapan")) return "gapan";
  if (s.includes("bulacan")) return "bulacan";
  return s;
}

export function parseMoneyAmount(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const cleaned = v.replace(/[^0-9.-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function normalizeMonthKey(s) {
  return String(s || "").trim().toLowerCase();
}

function monthIndexFromName(name) {
  const m = normalizeMonthKey(name);
  const map = {
    january: 0,
    february: 1,
    march: 2,
    april: 3,
    may: 4,
    june: 5,
    july: 6,
    august: 7,
    september: 8,
    october: 9,
    november: 10,
    december: 11
  };
  if (m in map) return map[m];
  const short = m.slice(0, 3);
  const shortMap = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
  };
  return short in shortMap ? shortMap[short] : null;
}

function formatYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Try to match a level configuration based on a student's grade level string
export function matchLevelConfig(campus, gradeLevel) {
  if (!campus || !gradeLevel) return null;
  const campusKey = normalizeCampusKey(campus);
  const campusConfig = tuitionConfigs[campusKey];
  if (!campusConfig) return null;

  const gl = String(gradeLevel).toLowerCase();
  const gradeMatch = gl.match(/\d+/);
  let gradeNumber = gradeMatch ? parseInt(gradeMatch[0], 10) : null;
  // Handle Nursery/Pre-Kinder/Kinder/Pre-Elem by mapping them to Grade 1 bucket
  if (gradeNumber == null) {
    if (/nursery|pre[-\s]?kinder|pre[-\s]?elem|kinder/.test(gl)) {
      const pre = (campusConfig.levels || []).find(l => String(l.name).toLowerCase().includes("pre-elementary"));
      if (pre) return pre;
      gradeNumber = 1;
    }
  }
  if (!gradeNumber) return null;

  const levels = campusConfig.levels || [];
  for (const lvl of levels) {
    const name = String(lvl.name || "");
    // Match ranges like "Grades 7-10" or "Grade 7 - 10"
    const rangeMatch =
      name.match(/Grade[s]?\s*(\d+)\s*-\s*(\d+)/i) ||
      name.match(/Grade[s]?\s*(\d+)\s*[,&-]*\s*Grade[s]?\s*(\d+)/i);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      if (gradeNumber >= min && gradeNumber <= max) return lvl;
    }
    // Match "Pre-Elem to Grade X" or "Pre-School to Grade X"
    const preElemMatch = name.match(/Pre-(?:Elem|School) to Grade\s*(\d+)/i);
    if (preElemMatch) {
      const max = parseInt(preElemMatch[1], 10);
      if (gradeNumber <= max) return lvl;
    }
    // Match single grade "Grade X"
    const singleMatch = name.match(/Grade\s*(\d+)/i);
    if (singleMatch) {
      const g = parseInt(singleMatch[1], 10);
      if (gradeNumber === g) return lvl;
    }
  }
  return null;
}

export function getPaymentModeConfig(levelConfig, paymentMode) {
  if (!levelConfig) return null;
  const modes = levelConfig.paymentModes || [];
  return modes.find(pm => pm.mode === paymentMode) || null;
}

export function computeTotalDue(pmConfig) {
  if (!pmConfig) return 0;
  const sched = pmConfig.schedule || [];
  return sched.reduce((sum, s) => sum + Number(s.misc || 0) + Number(s.tuition || 0), 0);
}

export function computeStudentFinance(student, paymentsForStudent, paymentModeFallback = "Annual") {
  const paymentMode = student.paymentMode || paymentModeFallback;
  const campus = normalizeCampusKey(student.campus);
  let levelConfig = null;
  const esc = Boolean(student.escProgram || student.esc || student.hasEsc || student.voucherEsc);
  if (student.levelName) {
    levelConfig = (tuitionConfigs[campus]?.levels || []).find(l => l.name === student.levelName) || null;
  }
  if (!levelConfig) {
    const gl = String(student.gradeLevel || "").toLowerCase();
    const m = gl.match(/\d+/);
    const gnum = m ? parseInt(m[0], 10) : null;
    if (esc && gnum && gnum >= 7 && gnum <= 10) {
      const escLevel = (tuitionConfigs[campus]?.levels || []).find(l => String(l.name).toLowerCase().includes("grades 7-10") && String(l.name).toLowerCase().includes("esc"));
      levelConfig = escLevel || matchLevelConfig(campus, student.gradeLevel);
    } else {
      levelConfig = matchLevelConfig(campus, student.gradeLevel);
    }
  }
  let totalDue = 0;
  let schedule = [];
  if (levelConfig) {
    const useCustom =
      typeof student.applyCustomDiscount === "boolean" && student.applyCustomDiscount === true &&
      typeof student.customDiscountPercent === "number" && !isNaN(student.customDiscountPercent);
    const sched = getStudentPaymentSchedule({
      campus,
      levelName: levelConfig.name,
      paymentMode,
      customDiscount: useCustom ? (student.customDiscountPercent / 100) : null
    });
    if (sched) {
      totalDue = Number(sched.totalDue || 0);
      schedule = sched.schedule || [];
    } else {
      const pmConfig = getPaymentModeConfig(levelConfig, paymentMode);
      totalDue = computeTotalDue(pmConfig);
      schedule = pmConfig?.schedule || [];
    }
  }
  const totalPaid = (paymentsForStudent || []).reduce((sum, p) => sum + parseMoneyAmount(p.amount), 0);
  const outstanding = Math.max(0, totalDue - totalPaid);
  const nextInfo = computeNextDue(schedule, paymentsForStudent || []);
  const nextDue = typeof nextInfo === "number" ? nextInfo : Number(nextInfo.amount || 0);
  const nextLabel = typeof nextInfo === "object" ? (nextInfo.label || "") : "";
  return { paymentMode, totalDue, totalPaid, outstanding, schedule, nextDue, nextLabel };
}

export function computeScheduleRemainingLines(schedule, payments) {
  if (!Array.isArray(schedule) || schedule.length === 0) return [];
  const lines = schedule.map(s => ({
    label: s.label || s.period || "",
    total: Number(s.total || (Number(s.misc || 0) + Number(s.tuition || 0))) || 0
  }));
  const reservation = (payments || [])
    .filter(p => String(p.category || "").toLowerCase() === "reservation")
    .reduce((sum, p) => sum + parseMoneyAmount(p.amount), 0);
  const otherPays = (payments || [])
    .filter(p => String(p.category || "").toLowerCase() !== "reservation")
    .reduce((sum, p) => sum + parseMoneyAmount(p.amount), 0);
  let remainingRes = reservation;
  const remainingLines = lines.map(l => ({ ...l, remaining: l.total }));
  for (const l of remainingLines) {
    if (remainingRes <= 0) break;
    const used = Math.min(remainingRes, l.remaining);
    l.remaining -= used;
    remainingRes -= used;
  }
  let paid = otherPays;
  for (const l of remainingLines) {
    if (paid <= 0) break;
    const used = Math.min(paid, l.remaining);
    l.remaining -= used;
    paid -= used;
  }
  return remainingLines;
}

export function computeRemainingForLabel(schedule, payments, labelSubstr) {
  const target = String(labelSubstr || "").toLowerCase();
  const remainingLines = computeScheduleRemainingLines(schedule, payments);
  const targetLine = remainingLines.find(l => String(l.label || "").toLowerCase().includes(target));
  return targetLine ? targetLine.remaining : 0;
}
export function calculateFee(amount, type) {
  if (type === "tuition") {
    return amount * 0.95;
  } else if (type === "misc") {
    return amount * 0.98;
  }
  return amount;
}

// Apply reservation and tuition payments to the schedule to compute remaining amount for the next due period.
function computeNextDue(schedule, payments) {
  const remainingLines = computeScheduleRemainingLines(schedule, payments);
  if (remainingLines.length === 0) return { amount: 0, label: "" };
  const next = remainingLines.find(l => l.remaining > 0);
  return next ? { amount: next.remaining, label: next.label } : { amount: 0, label: "" };
}

export function computeDueAsOfMonth(schedule, payments, monthName, options = {}) {
  const remainingLines = computeScheduleRemainingLines(schedule, payments);
  if (remainingLines.length === 0) {
    return { uponEnrollmentTotal: 0, targetTotal: 0, targetRemaining: 0, dueAmount: 0, dueDate: "", targetLabel: "" };
  }
  const scheduleLines = (Array.isArray(schedule) ? schedule : []).map(s => ({
    label: s.label || s.period || "",
    total: Number(s.total || (Number(s.misc || 0) + Number(s.tuition || 0))) || 0
  }));
  const target = String(monthName || "").trim();
  const targetLC = target.toLowerCase();

  let targetIndex = remainingLines.findIndex(l => String(l.label || "").toLowerCase().includes(targetLC));
  if (targetIndex < 0) {
    const mi = monthIndexFromName(target);
    const startMonth = options.startMonth || "June";
    const startMi = monthIndexFromName(startMonth);
    if (mi != null && startMi != null) {
      const offset = (mi - startMi + 12) % 12;
      if (remainingLines.length === 1) targetIndex = 0;
      else if (remainingLines.length === 2) targetIndex = offset >= 5 ? 1 : 0;
      else {
        const hasEnrollmentFirst = scheduleLines.length > 1 && /upon\s+enrollment/i.test(String(scheduleLines[0]?.label || ""));
        const idx = hasEnrollmentFirst ? (offset + 1) : offset;
        targetIndex = Math.min(remainingLines.length - 1, idx);
      }
    }
  }
  if (targetIndex < 0) targetIndex = 0;

  const targetLine = remainingLines[targetIndex] || remainingLines[0];
  const targetRemaining = Number(targetLine?.remaining || 0);
  const dueAmount = remainingLines.slice(0, targetIndex + 1).reduce((sum, l) => sum + Number(l.remaining || 0), 0);
  const uponEnrollmentTotal = Number(scheduleLines[0]?.total || 0);
  const targetTotal = Number(scheduleLines[targetIndex]?.total || scheduleLines[0]?.total || 0);

  let dueDate = "";
  const mi = monthIndexFromName(target);
  if (mi != null) {
    const startMi = monthIndexFromName(options.startMonth || "June");
    const explicitStartYear = Number(options.startYear);
    const today = options.today instanceof Date ? options.today : new Date();
    const derivedStartYear = today.getMonth() >= (startMi ?? 5) ? today.getFullYear() : today.getFullYear() - 1;
    const startYear = Number.isFinite(explicitStartYear) && explicitStartYear > 1900 ? explicitStartYear : derivedStartYear;
    const dueYear = mi >= (startMi ?? 5) ? startYear : startYear + 1;
    const first = new Date(dueYear, mi, 1);
    dueDate = formatYMDLocal(first);
  }

  return { uponEnrollmentTotal, targetTotal, targetRemaining, dueAmount, dueDate, targetLabel: String(targetLine?.label || "") };
}
